<script setup lang="ts">
import { computed, inject, onUnmounted } from "vue";
import { ActionButtonFormElement, ActionButtonType } from "~/api";
import {
  workflowInjectKey,
  WorkflowState,
} from "~/Compliance/composables/useWorkflow";
import { customActionButtonInjectKey } from "~/NewFormBuilder/composables/useCustomActionButton";
import {
  formResponseInjectKey,
  FormResponseState,
} from "~/NewFormBuilder/composables/useFormResponse";
import { usePdfLoadingStore } from "~/stores/pdfLoadingStore";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";
import { hubManager } from "~/utils/hubManager";
import type { DefaultFormComponentProps } from "./FormComponentRenderer.vue";

const props = defineProps<DefaultFormComponentProps<ActionButtonFormElement>>();

const elementProperties = computed(() => props.modelValue.properties);

const { isReadOnly, flushPendingAnswerSaves } = inject(
  formResponseInjectKey,
  // Cast the type since we know it is always provided. For more info see:
  // https://vuejs.org/guide/typescript/composition-api.html#typing-provide-inject
) as FormResponseState;

const { getWorkflow } = inject(workflowInjectKey, {
  updateWorkflowStatus: async (status: string) => {
    console.log("No workflow in designer. Status to update to:", status);
  },
  updateWorkflowStatusFromNameStamp: async (status: string) => {
    console.log("No workflow in designer. Status to update to:", status);
  },
  getWorkflowStatus: () => {
    return "";
  },
  getWorkflow: () => {
    return undefined;
  },
}) as WorkflowState;

const customActions = inject(customActionButtonInjectKey, {
  handleCustomAction: (code: string) => {
    console.warn("No custom action handler registered for code:", code);
  },
});

// to access the current workflow that the PDF will be generated for
const pdfLoadingStore = usePdfLoadingStore();

const workflowGuid = computed(
  () => getWorkflow()?.guid || pdfLoadingStore.workflowGuid || undefined,
);

const makePdf = async (pdfCode?: string) => {
  if (!workflowGuid.value) {
    showError("Workflow instance not found");
    return;
  }

  // Ensure we pass the ConnectionId for the "self" hub (where PdfMsg listener is).
  await hubManager.getHub("self", "/self");
  const selfConnId =
    hubManager.getHubConnectionId("self") ?? hubManager.ConnectionId ?? "";
  await pdfLoadingStore.makePdf(pdfCode, workflowGuid.value, selfConnId);
};

const sendEmail = async () => {
  if (!workflowGuid.value) {
    showError("Workflow instance not found");
    return;
  }
  const eventCode = elementProperties.value.code;
  if (!eventCode) {
    showError("Event code is missing.");
    return;
  }
  await apiClient.workflowInstance.handleSendEmailEvent(
    workflowGuid.value,
    eventCode,
  );
};

const runPeopleMigration = async () => {
  if (!workflowGuid.value) {
    showError("Workflow instance not found");
    return;
  }
  // Migration re-reads the saved answers server-side, so make sure any pending
  // debounced saves have persisted first — otherwise it reads stale values.
  await flushPendingAnswerSaves();
  try {
    const result = await apiClient.workflowInstance.handleRunPeopleMigration(
      workflowGuid.value,
    );
    console.log(
      `People migration complete: ${result.peopleCreated} created, ${result.skipped} skipped`,
    );
  } catch (e) {
    console.error("People migration failed.", e);
  }
};

onUnmounted(() => {
  pdfLoadingStore.reset();
});

const handleButtonClicked = async () => {
  if (elementProperties.value.actionToTake === ActionButtonType.GET_PDF) {
    if (elementProperties.value.code) {
      await makePdf(elementProperties.value.code);
    } else {
      await makePdf();
    }
  } else if (
    elementProperties.value.actionToTake === ActionButtonType.SEND_EMAIL
  ) {
    await sendEmail();
  } else if (elementProperties.value.actionToTake === ActionButtonType.CUSTOM) {
    customActions.handleCustomAction(elementProperties.value.code ?? "");
  } else if (
    elementProperties.value.actionToTake ===
    ActionButtonType.RUN_PEOPLE_MIGRATION
  ) {
    await runPeopleMigration();
  } else {
    showError(
      "Unsupported action type:" + elementProperties.value.actionToTake,
    );
  }
};
</script>

<template id="form-action-button-component">
  <div class="ActionButtonComponent">
    <slot name="label" :element="modelValue" />
    <el-button
      type="primary"
      :disabled="isReadOnly(modelValue)"
      @click="handleButtonClicked"
    >
      {{ elementProperties.actionButtonText ?? "Submit" }}</el-button
    >
  </div>
</template>

<style lang="less">
.ActionButtonComponent {
  display: flex;
  justify-content: space-between;
}
</style>
